// src/pages/HistoricoVendas/HistoricoVendas.js
import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../auth/AuthContext';
import './HistoricoVendas.css';

// Função para formatar a data para o input (YYYY-MM-DD)
const getFormattedDate = (date) => {
  return date.toISOString().split('T')[0];
};

function HistoricoVendas() {
  const [vendas, setVendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentUser, userRole } = useAuth();
  
  // 1. Estados para o filtro de data, inicializados com a data de hoje
  const [dataInicio, setDataInicio] = useState(getFormattedDate(new Date()));
  const [dataFim, setDataFim] = useState(getFormattedDate(new Date()));

  useEffect(() => {
    const buscarVendas = async () => {
      if (!currentUser || !dataInicio || !dataFim) {
        setLoading(false);
        return;
      }
      setLoading(true);

      try {
        let q;
        const vendasCollection = collection(db, "vendas");

        // Converte as strings de data para objetos Date do JS para a consulta
        const start = new Date(dataInicio);
        start.setHours(0, 0, 0, 0); // Início do dia
        const end = new Date(dataFim);
        end.setHours(23, 59, 59, 999); // Fim do dia

        // Constrói a consulta base
        const constraints = [
            where("dataHora", ">=", start),
            where("dataHora", "<=", end)
        ];

        if (userRole === 'vendedor') {
            constraints.push(where("vendedorId", "==", currentUser.uid));
        }
        
        // Adiciona a ordenação no final
        constraints.push(orderBy('dataHora', 'desc'));

        q = query(vendasCollection, ...constraints);
        
        const querySnapshot = await getDocs(q);
        const listaVendas = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            dataHora: data.dataHora ? data.dataHora.toDate() : new Date(),
          };
        });
        
        setVendas(listaVendas);

      } catch (error) {
        console.error("Erro ao buscar histórico de vendas:", error);
        alert("Não foi possível carregar o histórico de vendas. Pode ser necessário criar um índice no Firestore. Verifique o console para mais detalhes.");
      } finally {
        setLoading(false);
      }
    };

    buscarVendas();
  }, [currentUser, userRole, dataInicio, dataFim]); // Re-executa se as datas mudarem

  // 2. Cálculos para os relatórios usando useMemo para otimização
  const { totalVendasFiltradas, relatorioProdutos } = useMemo(() => {
    const produtosAgregados = {};
    let total = 0;

    vendas.forEach(venda => {
      total += venda.valorTotalVenda;
      venda.itens.forEach(item => {
        const chaveProduto = item.produtoId || item.nome;
        if (!produtosAgregados[chaveProduto]) {
          produtosAgregados[chaveProduto] = {
            nome: item.nome,
            quantidade: 0,
            totalVendido: 0,
          };
        }
        produtosAgregados[chaveProduto].quantidade += item.quantidade;
        produtosAgregados[chaveProduto].totalVendido += item.subtotal;
      });
    });

    const resultadoArray = Object.values(produtosAgregados)
      .sort((a, b) => b.totalVendido - a.totalVendido);

    return { totalVendasFiltradas: total, relatorioProdutos: resultadoArray };
  }, [vendas]); // Recalcula apenas quando a lista de vendas mudar

  if (loading) {
    return <div className="loading-container"><h1>Carregando histórico...</h1></div>;
  }

  return (
    <div className="historico-container">
      <h1>Histórico de Vendas</h1>
      
      {/* 3. Filtros de Data e Resumo de Vendas */}
      <div className="relatorio-header">
        <div className="filtro-datas">
          <label>De:</label>
          <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
          <label>Até:</label>
          <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
        </div>
        <div className="sumario-vendas">
          <h3>Total no Período</h3>
          <p>R$ {totalVendasFiltradas.toFixed(2)}</p>
        </div>
      </div>

      {/* 4. Relatório de Vendas por Produto */}
      <div className="relatorio-produtos-section">
        <h4>Vendas por Produto</h4>
        {relatorioProdutos.length === 0 ? (
          <p>Nenhum produto vendido no período.</p>
        ) : (
          <table className="dados-table">
            <thead>
              <tr>
                <th>Produto</th>
                <th>Quantidade Vendida</th>
                <th>Receita Gerada</th>
              </tr>
            </thead>
            <tbody>
              {relatorioProdutos.map(produto => (
                <tr key={produto.nome}>
                  <td>{produto.nome}</td>
                  <td>{produto.quantidade}</td>
                  <td>R$ {produto.totalVendido.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Tabela de Vendas Detalhadas (existente) */}
      <div className="tabela-vendas-wrapper">
        <h4>Vendas Detalhadas</h4>
        <table className="tabela-vendas dados-table">
          <thead>
            <tr>
              <th>Data e Hora</th>
              <th>Itens Vendidos</th>
              {userRole === 'admin' && <th>Vendedor</th>}
              <th>Valor Total</th>
            </tr>
          </thead>
          <tbody>
            {vendas.length === 0 ? (
              <tr>
                <td colSpan={userRole === 'admin' ? 4 : 3}>Nenhuma venda encontrada.</td>
              </tr>
            ) : (
              vendas.map(venda => (
                <tr key={venda.id}>
                  <td>{venda.dataHora.toLocaleString('pt-BR')}</td>
                  <td>
                    <ul>
                      {venda.itens.map((item, index) => (
                        <li key={index}>
                          {item.quantidade}x {item.nome} ({item.tamanho})
                        </li>
                      ))}
                    </ul>
                  </td>
                  {userRole === 'admin' && <td>{venda.vendedorEmail}</td>}
                  <td>R$ {venda.valorTotalVenda.toFixed(2)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default HistoricoVendas;
